import { equals } from '@execonline-inc/collections';
import { emptyFragment } from '@execonline-inc/execonline-ui';
import { when } from '@execonline-inc/maybe-adapter';
import { observer } from 'mobx-react';
import * as React from 'react';
import SetCurrentUser from '../../../CurrentUser/Context/SetCurrentUser';
import { currentUserStore } from '../../../CurrentUser/Store';
import ThemeContext, { orgSpecificLoginPageTheme } from '../../../Themes/ThemeContext';
import AutoLaunchable from '../../../components/AutoLaunchable';
import {
  AutoLaunchableContext,
  AutoLaunchableSource,
} from '../../../components/AutoLaunchable/AutoLaunchableContext';
import { LinkableProvider } from '../../../components/Linkable/Context';
import Loader from '../../../components/Loader';
import RegisterResourceStore from '../DiscoveryPortal/RegisterResourceStore';
import RequireAuthentication from '../RequireAuthentication';
import Enrollment from './Enrollment';
import { usePreviewContext } from './PreviewMode/usePreviewContext';
import { RegisterResource } from './RegisterResourceStore/Types';

interface Props {
  registerResource: RegisterResource;
  registerResourceStore: RegisterResourceStore;
}

const DiscoveryPortalImpl: React.FC<Props> = ({ registerResourceStore, registerResource }) => {
  const previewMode = usePreviewContext();

  switch (currentUserStore.state.kind) {
    case 'waiting':
    case 'loading':
    case 'logging-out':
      return <Loader delay={0} />;
    case 'anonymous':
    case 'errored':
      return (
        <ThemeContext.Provider
          value={orgSpecificLoginPageTheme(registerResource.payload.sharedInvitation)}
        >
          <RequireAuthentication
            registerResource={registerResource}
            readyLockedAccess={registerResourceStore.readyLockedAccess}
          />
        </ThemeContext.Provider>
      );
    case 'ready':
    case 'refreshing':
      const contextValue: AutoLaunchableSource = registerResource.payload.autoEnrollProgramId
        .map<AutoLaunchableSource>(() => 'share_url_with_auto_enroll_program')
        .getOrElse(() => 'discovery_portal');
      return (
        <SetCurrentUser currentUserResource={currentUserStore.state.currentUserResource}>
          <LinkableProvider>
            <AutoLaunchableContext.Provider value={contextValue}>
              {when(equals(previewMode, 'default'), {})
                .map(() => <AutoLaunchable />)
                .getOrElse(emptyFragment)}
            </AutoLaunchableContext.Provider>
            <Enrollment
              registerResource={registerResource}
              registerResourceStore={registerResourceStore}
            />
          </LinkableProvider>
        </SetCurrentUser>
      );
  }
};

export default observer(DiscoveryPortalImpl);
